/**
 * SubjectWiseAnalysis – Subject-wise performance deep dive.
 *
 * Filter flow:
 *   1. Class  (10 / 12)
 *   2. Year
 *   3. Level  (National | Region | KV)
 *   4. View   (Graphical | Tabular)
 *   5. [Region / KV] Subject selector
 *   6. [KV] Region filter
 *   7. Metric (Pass Stats | PI | Overall)
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Trophy,
  AlertTriangle,
  Globe,
  MapPin,
  Building2,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Layers,
  TrendingUp,
  GitCompare,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  LabelList,
} from 'recharts';
import {
  useGetResultAllRoSubjectWiseQuery,
  useGetResultAllKvSubjectWiseQuery,
  useGetResultYearValueDropdownQuery,
} from '../services/api';
import type { SubjectWiseItem } from '../services/api';
import type { GradeTab, MetricMode, ViewMode } from '../components/classification/types';
import { GRADE_TABS, METRIC_MODES } from '../components/classification/constants';
import ViewModeToggle from '../components/classification/ViewModeToggle';
import SectionLabel from '../components/classification/SectionLabel';
import SubjectTable from '../components/classification/SubjectTable';
import KVSLoader from '../../../components/loaders/KVSLoader';

// ─── Types & constants ────────────────────────────────────────────────────────────────

type LevelMode = 'national' | 'region' | 'kv';

interface EntityItem {
  name: string;
  code?: string;
  region?: string;
  appeared: number;
  passed: number;
  failed: number;
  passPercentage: number;
  performanceIndex: number;
  avgMarks: number;
  maxMarks: number;
  minMarks: number;
  roRank: number | null;
  nationalRank: number | null;
  below33: number;
  range33to45: number;
  range45to60: number;
  range60to75: number;
  range75to90: number;
  above90: number;
  gradeA1: number;
  gradeA2: number;
  gradeB1: number;
  gradeB2: number;
  gradeC1: number;
  gradeC2: number;
  gradeD1: number;
  gradeD2: number;
  gradeE: number;
}

const LEVEL_MODES: { id: LevelMode; label: string; icon: React.ReactNode }[] = [
  { id: 'national', label: 'National', icon: <Globe className="w-4 h-4" /> },
  { id: 'region',   label: 'Region',   icon: <MapPin className="w-4 h-4" /> },
  { id: 'kv',       label: 'KV',       icon: <Building2 className="w-4 h-4" /> },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SubjectCardProps {
  item: SubjectWiseItem;
  rank: number;
  avg: { pass: number; pi: number };
  metricMode: MetricMode;
  onClick?: () => void;
}

const SubjectCard: React.FC<SubjectCardProps> = function ({ item, rank, avg, metricMode, onClick }) {
  const abovePass = item.passPercentage >= avg.pass;
  const abovePI = item.performanceIndex >= avg.pi;
  return (
    <div
      className={'bg-white rounded-xl border p-4 transition-all ' + (onClick ? 'cursor-pointer hover:border-[#F36B21] hover:shadow-md' : '')}
      onClick={onClick}
    >
      <div className="flex items-start mb-2">
        <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-sm font-bold flex items-center justify-center flex-shrink-0">
          {rank}
        </span>
      </div>
      <p className="text-sm font-semibold text-gray-800 leading-tight mb-1">{item.subjectName}</p>
      <p className="text-sm text-gray-400 mb-3">Code: {String(item.subjectCode)}</p>
      <div className="space-y-1.5">
        {metricMode !== 'pi' && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Pass %</span>
            <span className={'text-sm font-bold ' + (abovePass ? 'text-green-600' : 'text-red-500')}>
              {item.passPercentage.toFixed(1)}%
            </span>
          </div>
        )}
        {metricMode !== 'passStats' && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Perf. Index</span>
            <span className={'text-sm font-bold ' + (abovePI ? 'text-blue-600' : 'text-orange-500')}>
              {item.performanceIndex.toFixed(2)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Appeared</span>
          <span className="text-sm font-medium text-gray-700">{item.appeared.toLocaleString()}</span>
        </div>
        {item.avgMarks !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Avg Marks</span>
            <span className="text-sm font-medium text-gray-700">{item.avgMarks.toFixed(1)}</span>
          </div>
        )}
      </div>
      {/* mini pass bar */}
      <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-1.5 rounded-full transition-all"
          style={{
            width: item.passPercentage + '%',
            backgroundColor: abovePass ? '#22c55e' : '#ef4444',
          }}
        />
      </div>
    </div>
  );
};

// ─── Subject Bar Chart ────────────────────────────────────────────────────────

interface SubjectBarChartProps {
  data: SubjectWiseItem[];
  metric: 'passPercentage' | 'performanceIndex';
  title: string;
  avgValue: number;
  limit?: number;
}

const SubjectBarChartComponent: React.FC<SubjectBarChartProps> = function ({ data, metric, title, avgValue, limit = 30 }) {
  const sliced = data.slice(0, limit);
  const isPass = metric === 'passPercentage';
  const posColor = isPass ? '#22c55e' : '#3b82f6';
  const negColor = isPass ? '#f43f5e' : '#f97316';

  const chartData = sliced.map(function (s) {
    return {
      name: s.subjectName.length > 20 ? s.subjectName.substring(0, 18) + '\u2026' : s.subjectName,
      fullName: s.subjectName,
      value: +(s[metric] - avgValue).toFixed(2),
      actual: s[metric],
    };
  });
  const minWidth = Math.max(700, chartData.length * 90);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-1 gap-3">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
          {isPass ? 'Pass%' : 'PI'}: {avgValue.toFixed(1)}{isPass ? '%' : ''}
        </span>
      </div>
      <p className="text-sm text-gray-400 mb-4">Deviation from national average</p>
      <div className="overflow-x-auto pb-2">
        <div style={{ minWidth: minWidth + 'px' }}>
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={chartData} margin={{ top: 24, right: 12, left: 8, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval={0}
                height={90}
                angle={-35}
                textAnchor="end"
                tick={{ fontSize: 12, fill: '#667085' }}
              />
              <YAxis
                domain={[75 - avgValue, 'auto']}
                tick={{ fontSize: 13, fill: '#667085' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={function (v: number) { return (v + avgValue).toFixed(1) + (isPass ? '%' : ''); }}
              />
              <ReferenceLine y={0} stroke="#374151" strokeWidth={1.5} />
              <Tooltip
                content={function ({ active, payload }: any) {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
                      <p className="font-semibold text-gray-800 mb-1">{p.fullName}</p>
                      <p className="text-gray-600">Actual: <span className="font-bold">{p.actual}{isPass ? '%' : ''}</span></p>
                      <p style={{ color: p.value >= 0 ? posColor : negColor }}>
                        Dev: <span className="font-bold">{p.value >= 0 ? '+' : ''}{p.value}{isPass ? '%' : ''}</span>
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={36}>
                {chartData.map(function (e, i) {
                  return <Cell key={i} fill={e.value >= 0 ? posColor : negColor} fillOpacity={0.85} />;
                })}
                <LabelList
                  dataKey="actual"
                  position="top"
                  formatter={function (v: any) { return v + (isPass ? '%' : ''); }}
                  style={{ fontSize: 13, fill: '#374151' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ─── Subject Rankings ─────────────────────────────────────────────────────────

interface SubjectRankingPanelProps {
  items: SubjectWiseItem[];
  metric: 'passPercentage' | 'performanceIndex';
  type: 'top' | 'bottom';
  title: string;
}

const SubjectRankingPanel: React.FC<SubjectRankingPanelProps> = function ({ items, metric, type, title }) {
  const barColor = type === 'top' ? '#22c55e' : '#ef4444';
  const allVals = items.map(function (r) { return r[metric]; });
  const minVal = metric === 'passPercentage' ? Math.min(70, allVals.length ? Math.min.apply(null, allVals) : 70) : 0;
  const maxVal = allVals.length ? Math.max.apply(null, allVals) : 100;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        {type === 'top'
          ? <Trophy className="w-4 h-4 text-green-500" />
          : <AlertTriangle className="w-4 h-4 text-red-500" />}
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="space-y-3">
        {items.map(function (r, i) {
          return (
            <div key={String(r.subjectCode) + r.subjectName} className="flex items-start gap-3">
              <span
                className={
                  'w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ' +
                  (type === 'top'
                    ? i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-green-400'
                    : 'bg-red-400')
                }
              >
                {type === 'top' ? i + 1 : '!'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-0.5 gap-1">
                  <span className="text-sm font-medium text-gray-800 block truncate min-w-0" title={r.subjectName}>{r.subjectName}</span>
                  <span className="text-sm font-bold flex-shrink-0" style={{ color: barColor }}>
                    {r[metric].toFixed(1)}{metric === 'passPercentage' ? '%' : ''}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: (maxVal > minVal ? ((r[metric] - minVal) / (maxVal - minVal)) * 100 : 100) + '%', backgroundColor: barColor }}
                  />
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">No data</p>
        )}
      </div>
    </div>
  );
};

// ─── Entity Ranking Panel (Region / KV) ──────────────────────────────────────

interface EntityRankingPanelProps {
  items: EntityItem[];
  metric: 'passPercentage' | 'performanceIndex';
  type: 'top' | 'bottom';
  title: string;
}

const EntityRankingPanel: React.FC<EntityRankingPanelProps> = function ({ items, metric, type, title }) {
  const barColor = type === 'top' ? '#22c55e' : '#ef4444';
  const allVals = items.map(function (r) { return r[metric]; });
  const minVal = metric === 'passPercentage' ? Math.min(70, allVals.length ? Math.min.apply(null, allVals) : 70) : 0;
  const maxVal = allVals.length ? Math.max.apply(null, allVals) : 100;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        {type === 'top' ? <Trophy className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="space-y-3">
        {items.map(function (r, i) {
          return (
            <div key={r.name + (r.code ?? '')} className="flex items-start gap-3">
              <span className={'w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ' + (type === 'top' ? i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-green-400' : 'bg-red-400')}>
                {type === 'top' ? i + 1 : '!'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-0.5 gap-1">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-800 block truncate" title={r.name}>{r.name}</span>
                    {r.region && <span className="text-sm text-gray-400">{r.region}</span>}
                  </div>
                  <span className="text-sm font-bold flex-shrink-0" style={{ color: barColor }}>
                    {r[metric].toFixed(1)}{metric === 'passPercentage' ? '%' : ''}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full" style={{ width: (maxVal > minVal ? ((r[metric] - minVal) / (maxVal - minVal)) * 100 : 100) + '%', backgroundColor: barColor }} />
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No data</p>}
      </div>
    </div>
  );
};

// ─── Entity Bar Chart ─────────────────────────────────────────────────────────

interface EntityBarChartProps {
  data: EntityItem[];
  metric: 'passPercentage' | 'performanceIndex';
  title: string;
  avgValue: number;
  levelMode: 'region' | 'kv';
  limit?: number;
}

const EntityBarChart: React.FC<EntityBarChartProps> = function ({ data, metric, title, avgValue, levelMode, limit = 30 }) {
  const sliced = data.slice(0, limit);
  const isPass = metric === 'passPercentage';
  const posColor = isPass ? '#22c55e' : '#3b82f6';
  const negColor = isPass ? '#f43f5e' : '#f97316';

  const chartData = sliced.map(function (e) {
    return {
      name: e.name.length > 18 ? e.name.substring(0, 16) + '\u2026' : e.name,
      fullName: e.name,
      region: e.region,
      value: +(e[metric] - avgValue).toFixed(2),
      actual: e[metric],
    };
  });
  const minWidth = Math.max(600, chartData.length * (levelMode === 'kv' ? 55 : 80));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-1 gap-3">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        <span className="text-sm font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
          {isPass ? 'Pass%' : 'PI'}: {avgValue.toFixed(1)}{isPass ? '%' : ''}
        </span>
      </div>
      <p className="text-sm text-gray-400 mb-4">Deviation from average</p>
      <div className="overflow-x-auto pb-2">
        <div style={{ minWidth: minWidth + 'px' }}>
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={chartData} margin={{ top: 24, right: 12, left: 8, bottom: 90 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval={0}
                height={100}
                angle={-40}
                textAnchor="end"
                tick={{ fontSize: 13, fill: '#667085' }}
              />
              <YAxis
                domain={[75 - avgValue, 'auto']}
                tick={{ fontSize: 13, fill: '#667085' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={function (v: number) { return (v + avgValue).toFixed(1) + (isPass ? '%' : ''); }}
              />
              <ReferenceLine y={0} stroke="#374151" strokeWidth={1.5} />
              <Tooltip
                content={function ({ active, payload }: any) {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm min-w-[180px]">
                      <p className="font-semibold text-gray-800 mb-1">{p.fullName}</p>
                      {p.region && <p className="text-sm text-gray-400 mb-1">{p.region}</p>}
                      <p className="text-gray-600">Actual: <span className="font-bold">{p.actual}{isPass ? '%' : ''}</span></p>
                      <p style={{ color: p.value >= 0 ? posColor : negColor }}>
                        Dev: <span className="font-bold">{p.value >= 0 ? '+' : ''}{p.value}{isPass ? '%' : ''}</span>
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={32}>
                {chartData.map(function (e, i) {
                  return <Cell key={i} fill={e.value >= 0 ? posColor : negColor} fillOpacity={0.85} />;
                })}
                <LabelList
                  dataKey="actual"
                  position="top"
                  formatter={function (v: any) { return v + (isPass ? '%' : ''); }}
                  style={{ fontSize: 13, fill: '#374151' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ─── Entity Table (Region / KV) ───────────────────────────────────────────────

type EntitySortKey = 'name' | 'appeared' | 'passed' | 'failed' | 'passPercentage' | 'performanceIndex' | 'avgMarks' | 'maxMarks' | 'minMarks' | 'roRank' | 'nationalRank';

interface EntityTableProps {
  data: EntityItem[];
  levelMode: 'region' | 'kv';
}

const EntityTable: React.FC<EntityTableProps> = function ({ data, levelMode }) {
  const [sortKey, setSortKey] = useState<EntitySortKey>('passPercentage');
  const [sortAsc, setSortAsc]   = useState(false);
  const [search, setSearch]     = useState('');
  const [topBottomMode, setTopBottomMode] = useState<'none' | 'top' | 'bottom'>('none');
  const [topBottomN, setTopBottomN] = useState('10');
  const [passMin, setPassMin] = useState('');
  const [passMax, setPassMax] = useState('');
  const [piMin, setPiMin] = useState('');
  const [piMax, setPiMax] = useState('');

  const avgPass = data.length ? data.reduce(function (s, r) { return s + r.passPercentage; }, 0) / data.length : 0;
  const avgPI   = data.length ? data.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / data.length : 0;

  function handleSort(k: EntitySortKey) {
    if (sortKey === k) setSortAsc(!sortAsc); else { setSortKey(k); setSortAsc(false); }
  }

  const linkedSecKey: EntitySortKey | null =
    sortKey === 'performanceIndex' ? 'passPercentage'
    : sortKey === 'passPercentage' ? 'performanceIndex'
    : null;

  const sortedFiltered = [...data]
    .filter(function (r) {
      const q = search.toLowerCase();
      return (
        r.name.toLowerCase().includes(q) ||
        (r.region ?? '').toLowerCase().includes(q) ||
        (r.code ?? '').toLowerCase().includes(q)
      );
    })
    .sort(function (a, b) {
      const av: unknown = (a as any)[sortKey];
      const bv: unknown = (b as any)[sortKey];
      if (av === null) return 1;
      if (bv === null) return -1;
      let primary: number;
      if (typeof av === 'number' && typeof bv === 'number') {
        primary = sortAsc ? av - bv : bv - av;
      } else {
        primary = sortAsc ? String(av ?? '').localeCompare(String(bv ?? '')) : String(bv ?? '').localeCompare(String(av ?? ''));
      }
      if (primary !== 0 || !linkedSecKey) return primary;
      const asc = (a as any)[linkedSecKey] as number;
      const bsc = (b as any)[linkedSecKey] as number;
      return sortAsc ? asc - bsc : bsc - asc;
    });

  const rangeFiltered = sortedFiltered.filter(function (r) {
    if (passMin !== '' && r.passPercentage < parseFloat(passMin)) return false;
    if (passMax !== '' && r.passPercentage > parseFloat(passMax)) return false;
    if (piMin !== '' && r.performanceIndex < parseFloat(piMin)) return false;
    if (piMax !== '' && r.performanceIndex > parseFloat(piMax)) return false;
    return true;
  });

  const n = Math.max(1, parseInt(topBottomN, 10) || 10);
  const displayRows =
    topBottomMode === 'top' ? rangeFiltered.slice(0, n)
    : topBottomMode === 'bottom' ? rangeFiltered.slice(-n)
    : rangeFiltered;

  function downloadCSV() {
    const headers = levelMode === 'kv'
      ? ['Natl.Rank', 'RO Rank', 'School Name', 'Code', 'Region', 'Appeared', 'Passed', 'Failed', 'Pass%', 'PI', 'Avg Marks', 'Max Marks', 'Min Marks', '<33', '33-44.9', '45-59.9', '60-74.9', '75-89.9', '>90', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'D1', 'D2', 'E']
      : ['Natl.Rank', 'Region', 'Appeared', 'Passed', 'Failed', 'Pass%', 'PI', 'Avg Marks', 'Max Marks', 'Min Marks', '<33', '33-44.9', '45-59.9', '60-74.9', '75-89.9', '>90', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'D1', 'D2', 'E'];
    const rows = displayRows.map(function (r) {
      const base = [
        r.nationalRank ?? '',
        ...(levelMode === 'kv' ? [r.roRank ?? '', `"${r.name.replace(/"/g, '""')}"`, r.code ?? '', `"${(r.region ?? '').replace(/"/g, '""')}"`] : [`"${r.name.replace(/"/g, '""')}"`]),
        r.appeared, r.passed, r.failed,
        r.passPercentage.toFixed(1), r.performanceIndex.toFixed(2),
        r.avgMarks > 0 ? r.avgMarks.toFixed(1) : '', r.maxMarks > 0 ? r.maxMarks : '', r.minMarks > 0 ? r.minMarks : '',
        r.below33, r.range33to45, r.range45to60, r.range60to75, r.range75to90, r.above90,
        r.gradeA1, r.gradeA2, r.gradeB1, r.gradeB2, r.gradeC1, r.gradeC2, r.gradeD1, r.gradeD2, r.gradeE,
      ];
      return base.join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${levelMode === 'kv' ? 'kv' : 'region'}-subject-performance.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function Th({ label, field }: { label: string; field: EntitySortKey }) {
    const isPrimary = sortKey === field;
    const isLinked = linkedSecKey === field;
    const Icon = isPrimary ? (sortAsc ? ArrowUp : ArrowDown) : isLinked ? (sortAsc ? ArrowUp : ArrowDown) : ArrowUpDown;
    const iconClass = isPrimary ? 'w-3 h-3 text-[#F36B21]' : isLinked ? 'w-3 h-3 text-orange-300' : 'w-3 h-3 text-gray-400';
    return (
      <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100" onClick={function () { handleSort(field); }}>
        <span className="flex items-center gap-1">
          {label}
          <Icon className={iconClass} />
        </span>
      </th>
    );
  }

  const colSpanEmpty = levelMode === 'kv' ? 27 : 25;
  const colSpanFoot  = levelMode === 'kv' ? 3 : 2;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <h3 className="text-lg font-semibold text-gray-800">{levelMode === 'kv' ? 'School-wise Performance' : 'Region-wise Performance'}</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{data.length} {levelMode === 'kv' ? 'KVs' : 'regions'}</span>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={levelMode === 'kv' ? 'Search school or KV code…' : 'Search region…'}
              value={search}
              onChange={function (e) { setSearch(e.target.value); }}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
        </div>
      </div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200 text-sm">
        <div className="flex rounded-lg overflow-hidden border border-gray-200">
          {(['none', 'top', 'bottom'] as const).map(function (mode) {
            return (
              <button key={mode} onClick={function () { setTopBottomMode(mode); }} className={'px-3 py-1.5 text-sm font-medium transition-colors ' + (topBottomMode === mode ? 'bg-[#F36B21] text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                {mode === 'none' ? 'All' : mode === 'top' ? 'Top' : 'Bottom'}
              </button>
            );
          })}
        </div>
        <input type="number" min="1" value={topBottomN} onChange={function (e) { setTopBottomN(e.target.value); }} disabled={topBottomMode === 'none'} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-40" placeholder="N" />
        <span className="text-gray-400 text-sm">Pass%</span>
        <input type="number" placeholder="Min" value={passMin} onChange={function (e) { setPassMin(e.target.value); }} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white" />
        <span className="text-gray-300">–</span>
        <input type="number" placeholder="Max" value={passMax} onChange={function (e) { setPassMax(e.target.value); }} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white" />
        <span className="text-gray-400 text-sm">PI</span>
        <input type="number" placeholder="Min" value={piMin} onChange={function (e) { setPiMin(e.target.value); }} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white" />
        <span className="text-gray-300">–</span>
        <input type="number" placeholder="Max" value={piMax} onChange={function (e) { setPiMax(e.target.value); }} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white" />
        <span className="ml-auto text-sm text-gray-400">{displayRows.length} rows</span>
        <button onClick={downloadCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F36B21] text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors">
          <Download className="w-3.5 h-3.5" />CSV
        </button>
      </div>
      <div className="overflow-auto max-h-[560px]">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-gray-50">
              <Th label="Natl. Rank" field="nationalRank" />
              {levelMode === 'kv' && <Th label="RO Rank" field="roRank" />
              }
              <Th label={levelMode === 'kv' ? 'School Name' : 'Region'} field="name" />
              {levelMode === 'kv' && <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Region</th>}
              <Th label="Appeared" field="appeared" />
              <Th label="Passed" field="passed" />
              <Th label="Failed" field="failed" />
              <Th label="Pass %" field="passPercentage" />
              <Th label="Perf. Index" field="performanceIndex" />
              <Th label="Avg Marks" field="avgMarks" />
              <Th label="Max Marks" field="maxMarks" />
              <Th label="Min Marks" field="minMarks" />
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap bg-red-50">&lt;33%</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap bg-orange-50">33-44.9%</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap bg-yellow-50">45-59.9%</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap bg-lime-50">60-74.9%</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap bg-green-50">75-89.9%</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap bg-emerald-50">&gt;90%</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">A1</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">A2</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">B1</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">B2</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">C1</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">C2</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">D1</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">D2</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">E</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayRows.map(function (item, idx) {
              const abovePass = item.passPercentage >= avgPass;
              const abovePI   = item.performanceIndex >= avgPI;
              return (
                <tr key={item.name + (item.code ?? '')} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 text-center">
                    {item.nationalRank != null
                      ? <span className="text-sm font-semibold text-blue-700">{item.nationalRank}</span>
                      : <span className="text-gray-400 text-sm">&#x2014;</span>}
                  </td>
                  {levelMode === 'kv' && (
                    <td className="py-3 px-4 text-center">
                      {item.roRank != null
                        ? <span className="text-sm font-semibold text-purple-700">{item.roRank}</span>
                        : <span className="text-gray-400 text-sm">&#x2014;</span>}
                    </td>
                  )}
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-gray-800">{item.name}</span>
                    {item.code && <span className="text-sm text-gray-400 ml-1.5">({item.code})</span>}
                  </td>
                  {levelMode === 'kv' && <td className="py-3 px-4 text-sm text-gray-500">{item.region ?? '\u2014'}</td>}
                  <td className="py-3 px-4 text-sm text-gray-700">{item.appeared.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.passed.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-red-500">{item.failed.toLocaleString()}</td>
                  <td className="py-3 px-4">
                    <span className={'text-sm font-bold ' + (abovePass ? 'text-green-600' : 'text-red-500')}>
                      {item.passPercentage.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={'text-sm font-bold ' + (abovePI ? 'text-blue-600' : 'text-orange-500')}>
                      {item.performanceIndex.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{item.avgMarks > 0 ? item.avgMarks.toFixed(1) : '\u2014'}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{item.maxMarks > 0 ? item.maxMarks : '\u2014'}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{item.minMarks > 0 ? item.minMarks : '\u2014'}</td>
                  <td className="py-3 px-4 text-sm text-red-600 bg-red-50">{item.below33.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-orange-600 bg-orange-50">{item.range33to45.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-yellow-700 bg-yellow-50">{item.range45to60.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-lime-700 bg-lime-50">{item.range60to75.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-green-700 bg-green-50">{item.range75to90.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-emerald-700 bg-emerald-50">{item.above90.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeA1.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeA2.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeB1.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeB2.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeC1.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeC2.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeD1.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeD2.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-700">{item.gradeE.toLocaleString()}</td>
                </tr>
              );
            })}
            {displayRows.length === 0 && (
              <tr><td colSpan={colSpanEmpty} className="py-12 text-center text-gray-400 text-sm">No results found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function SubjectWiseAnalysis() {
  const navigate = useNavigate();

  // ── Filters ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]                     = useState<GradeTab>('class10');
  const [selectedYear, setSelectedYear]               = useState<number>(2026);
  const [levelMode, setLevelMode]                     = useState<LevelMode>('national');
  const [viewMode, setViewMode]                       = useState<ViewMode>('graphical');
  const [metricMode, setMetricMode]                   = useState<MetricMode>('overall');
  const [selectedSubjectCode, setSelectedSubjectCode] = useState<number | null>(null);
  const [selectedROFilter, setSelectedROFilter]       = useState<string>('');

  const grade = activeTab === 'class10' ? 10 : 12;

  // ── Year dropdown ────────────────────────────────────────────────────────
  const { data: yearDropdownData } = useGetResultYearValueDropdownQuery({ grade });
  const yearOptions = useMemo(function () {
    const nums = (yearDropdownData || []).map(Number).filter(Number.isFinite);
    return nums.length > 0 ? nums : [selectedYear];
  }, [yearDropdownData, selectedYear]);

  React.useEffect(function () {
    if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset subject + RO filter when class or year changes
  React.useEffect(function () {
    setSelectedSubjectCode(null);
    setSelectedROFilter('');
  }, [grade, selectedYear]);

  // ── API calls ───────────────────────────────────────────────────────────
  // level=KVS → one aggregated row per subject (national); always fetched
  const { data: kvsData, isLoading: kvsLoading } = useGetResultAllKvSubjectWiseQuery({
    grade: grade as 10 | 12,
    year: selectedYear,
    level: 'KVS',
  });

  // level=RO → per-region rows; used for region view + roOptions dropdown
  const { data: roData, isLoading: roLoading } = useGetResultAllRoSubjectWiseQuery({
    grade: grade as 10 | 12,
    year: selectedYear,
  });

  // level=KV → per-KV rows; only fetched in KV mode
  const { data: kvData, isLoading: kvLoading } = useGetResultAllKvSubjectWiseQuery(
    { grade: grade as 10 | 12, year: selectedYear, level: 'KV', roname: selectedROFilter || undefined },
    { skip: levelMode !== 'kv' },
  );

  const subjectLoading = kvsLoading || roLoading || (levelMode === 'kv' && kvLoading);

  const metric: 'passPercentage' | 'performanceIndex' =
    metricMode === 'pi' ? 'performanceIndex' : 'passPercentage';
  const showPass = metricMode !== 'pi';
  const showPI   = metricMode !== 'passStats';

  // ── Subject options for selector dropdown ──────────────────────────────────
  // Derived from KVS data (one row per subject — no dedup needed)
  const subjectOptions = useMemo(function () {
    if (!kvsData) return [];
    return kvsData
      .map(function (item) { return { code: item.subjectCode, name: item.subjectName }; })
      .sort(function (a, b) { return a.name.localeCompare(b.name); });
  }, [kvsData]);

  // RO options for KV-level region filter
  const roOptions = useMemo(function () {
    if (!roData) return [];
    const seen = new Set<string>();
    roData.forEach(function (item) { seen.add(item.ro); });
    return Array.from(seen).sort();
  }, [roData]);

  // Auto-select first subject when switching to Region/KV
  React.useEffect(function () {
    if (levelMode !== 'national' && subjectOptions.length > 0 && selectedSubjectCode === null) {
      setSelectedSubjectCode(subjectOptions[0].code);
    }
  }, [levelMode, subjectOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── National: use KVS-level data directly (one pre-aggregated row per subject) ──
  const allSubjects = useMemo<SubjectWiseItem[]>(function () {
    if (!kvsData || kvsData.length === 0) return [];
    return [...kvsData].sort(function (a, b) { return b[metric] - a[metric]; });
  }, [kvsData, metric]);

  const avgPass = useMemo(function () {
    if (allSubjects.length === 0) return 0;
    return allSubjects.reduce(function (s, r) { return s + r.passPercentage; }, 0) / allSubjects.length;
  }, [allSubjects]);

  const avgPI = useMemo(function () {
    if (allSubjects.length === 0) return 0;
    return allSubjects.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / allSubjects.length;
  }, [allSubjects]);

  const avgMetric = metric === 'passPercentage' ? avgPass : avgPI;

  // ── Total summary stats ────────────────────────────────────────────────────
  const totalStats = useMemo(function () {
    if (allSubjects.length === 0) return null;
    const totalAppeared = allSubjects.reduce(function (s, r) { return s + r.appeared; }, 0);
    const totalPassed   = allSubjects.reduce(function (s, r) { return s + r.passed; }, 0);
    return {
      totalAppeared,
      totalPassed,
      passPercentage: totalAppeared > 0 ? +(totalPassed / totalAppeared * 100).toFixed(2) : 0,
      performanceIndex: +(allSubjects.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / allSubjects.length).toFixed(2),
    };
  }, [allSubjects]);

  // ── Region / KV entity lists ──────────────────────────────────────────────────
  const regionEntities = useMemo<EntityItem[]>(function () {
    if (!roData || selectedSubjectCode === null) return [];
    return roData
      .filter(function (item) { return item.subjectCode === selectedSubjectCode; })
      .map(function (item) {
        return {
          name: item.ro,
          appeared: item.appeared,
          passed: item.passed,
          failed: item.failed,
          passPercentage: item.passPercentage,
          performanceIndex: item.performanceIndex,
          avgMarks: item.avgMarks,
          maxMarks: item.maxMarks,
          minMarks: item.minMarks,
          roRank: item.roRank,
          nationalRank: item.nationalRank,
          below33: item.below33,
          range33to45: item.range33to45,
          range45to60: item.range45to60,
          range60to75: item.range60to75,
          range75to90: item.range75to90,
          above90: item.above90,
          gradeA1: item.gradeA1,
          gradeA2: item.gradeA2,
          gradeB1: item.gradeB1,
          gradeB2: item.gradeB2,
          gradeC1: item.gradeC1,
          gradeC2: item.gradeC2,
          gradeD1: item.gradeD1,
          gradeD2: item.gradeD2,
          gradeE: item.gradeE,
        };
      })
      .sort(function (a, b) { return b[metric] - a[metric]; });
  }, [roData, selectedSubjectCode, metric]);

  const kvEntities = useMemo<EntityItem[]>(function () {
    if (!kvData || selectedSubjectCode === null) return [];
    return kvData
      .filter(function (item) { return item.subjectCode === selectedSubjectCode; })
      .map(function (item) {
        return {
          name: item.kv ?? '',
          code: String(item.kvCode ?? ''),
          region: item.ro,
          appeared: item.appeared,
          passed: item.passed,
          failed: item.failed,
          passPercentage: item.passPercentage,
          performanceIndex: item.performanceIndex,
          avgMarks: item.avgMarks,
          maxMarks: item.maxMarks,
          minMarks: item.minMarks,
          roRank: item.roRank,
          nationalRank: item.nationalRank,
          below33: item.below33,
          range33to45: item.range33to45,
          range45to60: item.range45to60,
          range60to75: item.range60to75,
          range75to90: item.range75to90,
          above90: item.above90,
          gradeA1: item.gradeA1,
          gradeA2: item.gradeA2,
          gradeB1: item.gradeB1,
          gradeB2: item.gradeB2,
          gradeC1: item.gradeC1,
          gradeC2: item.gradeC2,
          gradeD1: item.gradeD1,
          gradeD2: item.gradeD2,
          gradeE: item.gradeE,
        };
      })
      .sort(function (a, b) { return b[metric] - a[metric]; });
  }, [kvData, selectedSubjectCode, metric]);

  const currentEntities    = levelMode === 'kv' ? kvEntities : regionEntities;
  
  // When in KV mode with no region filter, use region-level averages to match region view
  const statsEntities = (levelMode === 'kv' && selectedROFilter === '') ? regionEntities : currentEntities;
  const entityAvgPass      = statsEntities.length
    ? statsEntities.reduce(function (s, r) { return s + r.passPercentage; }, 0) / statsEntities.length : 0;
  const entityAvgPI        = statsEntities.length
    ? statsEntities.reduce(function (s, r) { return s + r.performanceIndex; }, 0) / statsEntities.length : 0;

  const selectedSubjectName = subjectOptions.find(function (s) { return s.code === selectedSubjectCode; })?.name ?? '';
  const entityTitle = selectedSubjectName
    ? selectedSubjectName + ' \u2014 ' + (levelMode === 'kv' ? 'School Rankings' : 'Region Rankings')
    : '';
  const displayTitle = activeTab === 'class10' ? 'Class 10 \u2014 All Subjects' : 'Class 12 \u2014 All Subjects';

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={function () { navigate('/result/classification'); }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#F36B21] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Classification
            </button>
          </div>
          <h1 className="text-2xl font-bold text-[#F36B21] flex items-center gap-2">
            <BookOpen className="w-7 h-7" />
            Subject-wise Analysis
          </h1>
          <p className="text-gray-500 text-sm mt-1">CBSE Board subject-wise performance deep dive</p>
        </div>
        <div className="flex flex-wrap gap-2.5 flex-shrink-0">
          <button
            onClick={function () { navigate('/result/classification'); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
          >
            <Layers className="w-4 h-4" />
            Classification Analysis
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={function () { navigate('/result/comparison'); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
          >
            <GitCompare className="w-4 h-4" />
            Class 10 & 12 Comparison
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={function () { navigate('/result/yearwise-analysis'); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
          >
            <TrendingUp className="w-4 h-4" />
            Year-wise Analysis
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Filter Panel ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">

        {/* Row 1: Class + Year + Subject */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <label className="text-sm font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">Class</label>
            <div className="relative">
              <select
                value={activeTab}
                onChange={function (e) { setActiveTab(e.target.value as GradeTab); }}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21] cursor-pointer"
              >
                {GRADE_TABS.map(function (tab) {
                  return <option key={tab.id} value={tab.id}>{tab.label}</option>;
                })}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <label className="text-sm font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">Year</label>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={function (e) { setSelectedYear(Number(e.target.value)); }}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21] cursor-pointer"
              >
                {yearOptions.map(function (y) {
                  return <option key={y} value={y}>{y - 1}–{String(y).slice(-2)}</option>;
                })}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" />
            </div>
          </div>

          {levelMode !== 'national' && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <label className="text-sm font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">Subject</label>
              <div className="relative">
                <select
                  value={selectedSubjectCode ?? ''}
                  onChange={function (e) { setSelectedSubjectCode(e.target.value ? Number(e.target.value) : null); }}
                  className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21] cursor-pointer min-w-[260px]"
                >
                  <option value="">— Select a subject —</option>
                  {subjectOptions.map(function (s) {
                    return <option key={s.code} value={s.code}>{s.name}</option>;
                  })}
                </select>
                <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" />
              </div>
            </div>
          )}
        </div>

        {/* Row 2: Level */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-28 flex-shrink-0">Level</span>
          <div className="flex flex-wrap gap-2">
            {LEVEL_MODES.map(function (lm) {
              return (
                <button
                  key={lm.id}
                  onClick={function () { setLevelMode(lm.id); }}
                  className={
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-semibold transition-all ' +
                    (levelMode === lm.id
                      ? 'bg-[#F36B21] text-white border-[#F36B21] shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#F36B21] hover:text-[#F36B21]')
                  }
                >
                  {lm.icon}
                  {lm.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 3: View toggle */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-28 flex-shrink-0">View</span>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>

        {/* Row 4: Metric */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-28 flex-shrink-0">Metric</span>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {METRIC_MODES.map(function (m) {
              return (
                <button
                  key={m.id}
                  onClick={function () { setMetricMode(m.id); }}
                  className={
                    'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ' +
                    (metricMode === m.id ? 'bg-white text-[#F36B21] shadow-sm' : 'text-gray-500 hover:text-gray-700')
                  }
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 5: Region filter (KV level only) */}
        {levelMode === 'kv' && (
          <div className="px-6 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-gray-400 uppercase tracking-wide w-28 flex-shrink-0">Region</span>
            <div className="relative">
              <select
                value={selectedROFilter}
                onChange={function (e) { setSelectedROFilter(e.target.value); }}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21] cursor-pointer min-w-[200px]"
              >
                <option value="">All Regions</option>
                {roOptions.map(function (ro) {
                  return <option key={ro} value={ro}>{ro}</option>;
                })}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" />
            </div>
          </div>
        )}
      </div>

      {/* ── Active filter badge row ────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Showing:</span>
        <span className="px-2 py-0.5 bg-orange-50 border border-orange-200 rounded-full text-orange-700 font-semibold">
          {activeTab === 'class10' ? 'Class 10' : 'Class 12'}
        </span>
        <span className="px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-blue-700 font-semibold">
          {selectedYear - 1}–{String(selectedYear).slice(-2)}
        </span>
        <span className="px-2 py-0.5 bg-orange-50 border border-orange-200 rounded-full text-orange-700 font-semibold">
          {LEVEL_MODES.find(function (l) { return l.id === levelMode; })?.label}
        </span>
        {levelMode !== 'national' && selectedSubjectName && (
          <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-full text-indigo-700 font-semibold">
            {selectedSubjectName}
          </span>
        )}
        {levelMode === 'kv' && selectedROFilter && (
          <span className="px-2 py-0.5 bg-green-50 border border-green-100 rounded-full text-green-700 font-semibold">
            {selectedROFilter}
          </span>
        )}
        <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded-full text-gray-700 font-semibold">
          {METRIC_MODES.find(function (m) { return m.id === metricMode; })?.label}
        </span>
        <span className="ml-auto text-gray-500 font-medium">
          {levelMode === 'national'
            ? allSubjects.length + ' subjects'
            : currentEntities.length + (levelMode === 'kv' ? ' KVs' : ' regions')}
        </span>
      </div>

      {/* ── Loading state ─────────────────────────────────────────────────── */}
      {subjectLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <KVSLoader size={80} color="#F36B21" />
            <p className="text-sm text-gray-400 mt-4">Loading subject data…</p>
          </div>
        </div>
      )}

      {!subjectLoading && (
        <>

          {/* ══════════════════ NATIONAL LEVEL ════════════════════════════════ */}
          {levelMode === 'national' && (
            <>
              {allSubjects.length === 0 && (
                <div className="bg-orange-50 border border-orange-100 rounded-xl p-8 text-center">
                  <BookOpen className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-orange-700">No subject data available</p>
                  <p className="text-sm text-orange-500 mt-1">No data returned for the selected class and year.</p>
                </div>
              )}

              {allSubjects.length > 0 && viewMode === 'graphical' && (
                <>
                  <div>
                    <SectionLabel>Subject Rankings — {displayTitle}</SectionLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <SubjectRankingPanel
                        items={allSubjects.slice(0, 5)}
                        metric={metric}
                        type="top"
                        title={'Top 5 — Highest ' + (metric === 'passPercentage' ? 'Pass %' : 'Perf. Index')}
                      />
                      <SubjectRankingPanel
                        items={[...allSubjects].reverse().slice(0, 5)}
                        metric={metric}
                        type="bottom"
                        title="Bottom 5 — Needs Attention"
                      />
                    </div>
                  </div>

                  <div>
                    <SectionLabel>All Subjects ({allSubjects.length})</SectionLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      {allSubjects.map(function (item, idx) {
                        return (
                          <SubjectCard
                            key={String(item.subjectCode) + item.subjectName}
                            item={item}
                            rank={idx + 1}
                            avg={{ pass: avgPass, pi: avgPI }}
                            metricMode={metricMode}
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {allSubjects.length > 0 && viewMode === 'tabular' && (
                <div>
                  <SectionLabel>Subject-wise Detail — {displayTitle}</SectionLabel>
                  <SubjectTable data={allSubjects} />
                </div>
              )}
            </>
          )}

          {/* ══════════════════ REGION / KV LEVEL ════════════════════════════ */}
          {levelMode !== 'national' && selectedSubjectCode === null && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-8 text-center">
              <BookOpen className="w-10 h-10 text-blue-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-blue-700">
                Select a subject to view {levelMode === 'kv' ? 'school' : 'region'}-wise rankings
              </p>
              <p className="text-sm text-blue-500 mt-1">Use the Subject filter above to drill into a specific subject.</p>
            </div>
          )}

          {levelMode !== 'national' && selectedSubjectCode !== null && (
            <>
              {/* Entity summary cards */}
              <div>
                <SectionLabel>
                  {selectedSubjectName} — {levelMode === 'kv' ? 'School' : 'Region'}-wise Summary
                </SectionLabel>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    {
                      label: levelMode === 'kv' ? 'KVs' : 'Regions',
                      value: String(currentEntities.length),
                      accent: 'text-[#F36B21]',
                    },
                    {
                      label: 'Total Appeared',
                      value: currentEntities.reduce(function (s, r) { return s + r.appeared; }, 0).toLocaleString(),
                      accent: 'text-blue-600',
                    },
                    {
                      label: 'Avg Pass %',
                      value: entityAvgPass.toFixed(1) + '%',
                      accent: entityAvgPass >= 80 ? 'text-green-600' : 'text-red-500',
                    },
                    {
                      label: 'Avg Perf. Index',
                      value: entityAvgPI.toFixed(2),
                      accent: 'text-purple-600',
                    },
                  ].map(function (c) {
                    return (
                      <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-1">{c.label}</p>
                        <p className={'text-2xl font-bold ' + c.accent}>{c.value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {currentEntities.length === 0 && (
                <div className="bg-orange-50 border border-orange-100 rounded-xl p-8 text-center">
                  <BookOpen className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-orange-700">No data for this subject</p>
                </div>
              )}

              {currentEntities.length > 0 && viewMode === 'graphical' && (
                <>
                  <div>
                    <SectionLabel>{entityTitle}</SectionLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <EntityRankingPanel
                        items={currentEntities.slice(0, 5)}
                        metric={metric}
                        type="top"
                        title={'Top 5 — Highest ' + (metric === 'passPercentage' ? 'Pass %' : 'Perf. Index')}
                      />
                      <EntityRankingPanel
                        items={[...currentEntities].reverse().slice(0, 5)}
                        metric={metric}
                        type="bottom"
                        title="Bottom 5 — Needs Attention"
                      />
                    </div>
                  </div>
                  <div className={'grid grid-cols-1 gap-4 ' + (showPass && showPI ? 'lg:grid-cols-2' : '')}>
                    {showPass && (
                      <EntityBarChart
                        data={currentEntities}
                        metric="passPercentage"
                        title={'Pass % — ' + entityTitle}
                        avgValue={entityAvgPass}
                        levelMode={levelMode as 'region' | 'kv'}
                        limit={50}
                      />
                    )}
                    {showPI && (
                      <EntityBarChart
                        data={currentEntities}
                        metric="performanceIndex"
                        title={'Performance Index — ' + entityTitle}
                        avgValue={entityAvgPI}
                        levelMode={levelMode as 'region' | 'kv'}
                        limit={50}
                      />
                    )}
                  </div>
                </>
              )}

              {currentEntities.length > 0 && viewMode === 'tabular' && (
                <EntityTable
                  data={currentEntities}
                  levelMode={levelMode as 'region' | 'kv'}
                />
              )}
            </>
          )}
        </>
      )}

    </div>
  );
}
